import type { AfterResponseHook, BeforeErrorHook, Options as KyOptions, KyResponse } from "ky";
import type { ResponseObject } from "@/generated/models";
import type { ResponseObjectOf } from "@/services/models/responseObject";
import * as v from "valibot";
import { isBoolean, isObject } from "@/common/general";

import { RpErrorTypes } from "@/generated/models";

export type UploadProgressHandler = KyOptions["onUploadProgress"];

export interface RequestOptions {
  signal?: AbortSignal;
  onUploadProgress?: UploadProgressHandler;
}

export interface ProblemDetailsException {
  details: string;
  endpoint: string;
  headers: Record<string, string[]>;
  path: string;
}

const ProblemDetailsSchema = v.object({
  detail: v.optional(v.string()),
  exception: v.optional(
    v.object({
      details: v.string(),
      endpoint: v.string(),
      headers: v.record(v.string(), v.array(v.string())),
      path: v.string(),
    }),
  ),
  instance: v.optional(v.string()),
  status: v.number(),
  title: v.string(),
  type: v.optional(v.string()),
});

const CloudRequestErrorExtensionSchema = v.object({
  isCloudResponseError: v.literal(true),
  cloudResponse: v.any(),
});

const CloudRequestErrorSchema = v.object({
  ...ProblemDetailsSchema.entries,
  ...CloudRequestErrorExtensionSchema.entries,
});

export type ProblemDetails = v.InferOutput<typeof ProblemDetailsSchema>;

export const isProblemDetails = (value: unknown) => v.is(ProblemDetailsSchema, value);
export const isCloudRequestErrorProblemDetails = (value: unknown) => v.is(CloudRequestErrorSchema, value);

// export function isProblemDetails(value: unknown): value is ProblemDetails {
//   if (!isObject(value)) return false;
//   return isNumber(value.status) && isString(value.title);
// }

function isCloudError(value: ResponseObject) {
  if (!isObject(value)) return false;
  if (!isBoolean(value.success)) return false;
  return !value.success;
}

export function isCloudResponse(value: unknown): value is ResponseObject {
  return isObject(value) && isBoolean(value.success);
}

function isCloudDataResponse(value: ResponseObject): value is ResponseObjectOf {
  return "data" in value;
}

const ErrorTypeToMessage: Partial<Record<RpErrorTypes, string>> = {
  [RpErrorTypes.ViewOnlyFilesystem]: "Failed to perform action: your workspace permissions are set to view only",
};

export function getErrorMessage(response: ResponseObject) {
  const errorMessage = response.errorMessage ?? response.message;
  if (errorMessage) {
    return errorMessage;
  }

  if (response.ErrorType != null) {
    const knownError = ErrorTypeToMessage[response.ErrorType];
    if (knownError) {
      return knownError;
    }
  }

  return "An unknown error occurred. Please try again later.";
}

export class AuthenticationFailedError extends Error {

}

export class FilesystemNotSharedError extends Error {

}

const TypeToErrorMap = {
  [RpErrorTypes.AuthenticationFailed]: AuthenticationFailedError,
  [RpErrorTypes.FilesystemNotSharedError]: FilesystemNotSharedError,
} as Record<RpErrorTypes, typeof Error | undefined>;

function tryParseJson(jsonText: string) {
  try {
    return JSON.parse(jsonText);
  } catch {
    return jsonText;
  }
}

export function parseCloudJsonResponse(text: string) {
  return unwrapCloudResponse(tryParseJson(text));
}

export function unwrapCloudResponse(json: unknown) {
  if (!isCloudResponse(json)) return json;

  if (isCloudError(json)) {
    const errorMessage = getErrorMessage(json);

    if (json.ErrorType != null) {
      const ErrorType = TypeToErrorMap[json.ErrorType];
      if (ErrorType) {
        throw new ErrorType(errorMessage, { cause: json });
      }
    }

    throw new Error(errorMessage, { cause: json });
  }

  if (isCloudDataResponse(json)) {
    return json.data;
  }

  return json;
}

export const problemDetailsErrorHook: BeforeErrorHook = async error => {
  if (!error.response) return error;

  const data = await error.response.json();
  if (!data) return error;

  if (!isProblemDetails(data)) {
    return error;
  }

  // there may be Cloud response error wrapped inside problem details
  if (isCloudRequestErrorProblemDetails(data)) {
    const cloudData = unwrapCloudResponse(data.cloudResponse);
    if (cloudData) {
      // Expected error but got success instead
      throw new Error(data.detail ?? data.title);
    }
    return error;
  }

  const problem = data;
  if (problem.exception) {
    error.message = problem.exception.details;
    return error;
  }

  error.message = problem.detail ?? problem.title;

  return error;
};

export const miniProfilerResponseHook: AfterResponseHook = async (_request, _options, response) => {
  miniProfilerHandleKyResponse(response);
};

function miniProfilerHandleKyResponse(response: KyResponse) {
  const miniProfiler = window.MiniProfiler;
  if (!miniProfiler || typeof miniProfiler.fetchResults !== "function") return response;

  const miniProfilerIdsHeader = response.headers.get("x-miniprofiler-ids");

  if (miniProfilerIdsHeader) {
    const profilerIds = JSON.parse(miniProfilerIdsHeader) as string[] | undefined;
    if (Array.isArray(profilerIds) && profilerIds.length) {
      miniProfiler.fetchResults(profilerIds);
    }
  }

  return response;
}
